import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileText, Loader2, Package, DollarSign, FileSpreadsheet, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

const num = (n) => new Intl.NumberFormat("es-MX", { maximumFractionDigits: 2 }).format(n || 0);
const money = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 }).format(n || 0);
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const now = new Date();
const YEARS = Array.from({ length: 7 }, (_, i) => now.getFullYear() - i);

export default function ComprasGYP() {
  const { currentStructure, structureKey, node } = useGlobal();
  const [mes, setMes] = useState(String(now.getMonth() + 1));
  const [anio, setAnio] = useState(String(now.getFullYear()));
  const [zona, setZona] = useState("all");
  const [cliente, setCliente] = useState("all");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const { data, isFetching } = useQuery({
    queryKey: ["compras-gyp", structureKey, node?.id, mes, anio, zona, cliente],
    queryFn: async () => (await api.get("/compras/gyp", { params: { structure: structureKey, node_id: node?.id, mes, anio, zona, cliente } })).data,
    enabled: !!structureKey,
    keepPreviousData: true,
  });
  const rows = data?.rows || [];
  const zonas = data?.zonas || [];
  const clientes = data?.clientes || [];

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const nums = new Set(["folio", "cantidad", "total"]);
    const arr = [...rows].sort((a, b) => {
      let x = a[sortKey], y = b[sortKey];
      if (nums.has(sortKey)) { x = Number(x) || 0; y = Number(y) || 0; return x - y; }
      return String(x || "").localeCompare(String(y || ""), "es", { numeric: true });
    });
    return sortDir === "desc" ? arr.reverse() : arr;
  }, [rows, sortKey, sortDir]);

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/compras/gyp/export", {
        params: { structure: structureKey, node_id: node?.id, mes, anio, zona, cliente },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compras_gyp_${anio}_${mes}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="compras-gyp-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Contabilidad</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white tracking-tight flex items-center gap-2">
          <FileText className="w-8 h-8 text-blue-400" /> Compras GYP
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Comprobantes de ingreso (cerrados) · {currentStructure?.name || "—"} ·{" "}
          <span className="text-base font-semibold text-slate-200" data-testid="gyp-sucursal">{node?.name || "Toda la estructura"}</span>
        </p>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl p-4 flex flex-wrap gap-3 items-center">
        <Select value={mes} onValueChange={setMes}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-44" data-testid="gyp-mes"><SelectValue placeholder="Mes" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
            <SelectItem value="all">Todo el año</SelectItem>
            {MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={anio} onValueChange={setAnio}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-32" data-testid="gyp-anio"><SelectValue placeholder="Año" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200">
            <SelectItem value="all">Todos</SelectItem>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={zona} onValueChange={(v) => { setZona(v); setCliente("all"); }}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-56" data-testid="gyp-zona"><SelectValue placeholder="Zona" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
            <SelectItem value="all">Todas las zonas</SelectItem>
            {zonas.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={cliente} onValueChange={setCliente}>
          <SelectTrigger className="h-10 bg-slate-950/60 border-slate-700 text-white w-56" data-testid="gyp-cliente"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200 max-h-72">
            <SelectItem value="all">Todos los clientes</SelectItem>
            {clientes.map((cl) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
          </SelectContent>
        </Select>
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        <div className="ml-auto flex flex-wrap gap-3 items-center">
          <Button onClick={exportExcel} disabled={exporting || !rows.length} data-testid="gyp-export-btn"
            className="group relative h-10 overflow-hidden rounded-lg bg-emerald-700 px-5 !text-white font-medium tracking-wide ring-1 ring-inset ring-emerald-500/30 shadow-sm transition-all duration-300 hover:bg-emerald-600 hover:ring-emerald-400/40 active:translate-y-px disabled:opacity-40 disabled:ring-white/10">
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
            <span className="relative flex items-center gap-2 text-white">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5" />}
              <span>{exporting ? "Generando…" : "Exportar a Excel"}</span>
              {!exporting && <Download className="w-3.5 h-3.5 opacity-0 -translate-x-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />}
            </span>
          </Button>
          <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/50 px-5 py-3 shadow-sm" data-testid="gyp-kpi-count">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-500/10 ring-1 ring-inset ring-slate-400/15">
              <FileText className="h-5 w-5 text-slate-300" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Comprobantes</p>
              <p className="text-2xl font-semibold leading-tight text-slate-100 tabular-nums">{num(data?.count)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/50 px-5 py-3 shadow-sm" data-testid="gyp-kpi-cantidad">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/20">
              <Package className="h-5 w-5 text-emerald-400/90" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cantidad</p>
              <p className="text-2xl font-semibold leading-tight text-slate-100 tabular-nums">{num(data?.total_cantidad)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/5 bg-slate-900/50 px-5 py-3 shadow-sm" data-testid="gyp-kpi-total">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 ring-1 ring-inset ring-amber-500/20">
              <DollarSign className="h-5 w-5 text-amber-400/90" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Total</p>
              <p className="text-2xl font-semibold leading-tight text-slate-100 tabular-nums">{money(data?.total_importe)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#131C2E] border border-[#223048] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#223048] hover:bg-transparent">
                {[
                  ["fecha", "Fecha", ""],
                  ["serie", "Serie", ""],
                  ["folio", "Folio", ""],
                  ["importadora", "Importadora", ""],
                  ["cliente", "Cliente", ""],
                  ["cantidad", "Cantidad", "text-right"],
                  ["total", "Total ($)", "text-right"],
                  ["folio_fiscal", "Folio fiscal (UUID)", ""],
                ].map(([key, label, align]) => (
                  <TableHead key={key} className={`text-slate-400 ${align}`}>
                    <button
                      type="button"
                      onClick={() => toggleSort(key)}
                      data-testid={`gyp-sort-${key}`}
                      className={`inline-flex items-center gap-1 hover:text-white transition-colors ${align === "text-right" ? "flex-row-reverse w-full justify-start" : ""}`}
                    >
                      <span>{label}</span>
                      {sortKey === key
                        ? (sortDir === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
                        : <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />}
                    </button>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((r, i) => (
                <TableRow key={`${r.serie}-${r.folio}-${i}`} className="border-[#223048] hover:bg-slate-900/40" data-testid={`gyp-row-${i}`}>
                  <TableCell className="text-slate-300 tabular-nums">{r.fecha || "—"}</TableCell>
                  <TableCell className="text-slate-400">{r.serie || "—"}</TableCell>
                  <TableCell className="text-white font-medium tabular-nums">{r.folio || "—"}</TableCell>
                  <TableCell className="text-slate-300">{r.importadora || "—"}</TableCell>
                  <TableCell className="text-slate-300">{r.cliente || "—"}</TableCell>
                  <TableCell className="text-right text-emerald-300 font-semibold tabular-nums">{num(r.cantidad)}</TableCell>
                  <TableCell className="text-right text-amber-300 font-semibold tabular-nums">{money(r.total)}</TableCell>
                  <TableCell className="text-slate-500 text-xs font-mono">{r.folio_fiscal || "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && (
                <TableRow className="border-t-2 border-[#223048] bg-[#0F172A]" data-testid="gyp-total-row">
                  <TableCell colSpan={5} className="text-white font-bold uppercase tracking-wide text-xs">Total general · {num(data?.count)} comprobantes</TableCell>
                  <TableCell className="text-right text-emerald-400 font-extrabold tabular-nums text-base">{num(data?.total_cantidad)}</TableCell>
                  <TableCell className="text-right text-amber-400 font-extrabold tabular-nums text-base">{money(data?.total_importe)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
              {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">Sin comprobantes GYP para el periodo y sucursal seleccionados.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
